import { useCallback, useMemo, useRef, useState } from "react";

/**
 * useListFilters — the state behind the FilterPanel pattern.
 *
 * Holds the drawer's `open` flag, the per-filter setters (each of which also
 * resets pagination), the `labelFor` lookup, and the active-filter array.
 *
 * ⚠️ The chip row, the trigger badge count and the drawer's applied count are all
 * derived from ONE array. That is the pattern's rule, and having it here is what
 * makes it structural rather than something each list page can get wrong alone.
 *
 * ── Usage ────────────────────────────────────────────────────────────────────
 *
 *   const f = useListFilters(
 *     {
 *       role:   { label: "Role",   options: ROLE_OPTIONS },
 *       status: { label: "Status", options: STATUS_OPTIONS, empty: "" },
 *     },
 *     () => setPage(1),          // optional: runs after any change or reset
 *   );
 *
 *   <FilterToolbar
 *     search={<SearchInput value={q} onChange={setQ} />}
 *     filters={<FilterButton count={f.count} onClick={f.open} />}
 *   />
 *   <ActiveFilterChips items={f.activeFilters} onClearAll={f.reset} />
 *   <FilterPanel {...f.panelProps}>
 *     <FilterSelectField {...f.selectProps("role")} />
 *     <FilterSelectField {...f.selectProps("status")} />
 *   </FilterPanel>
 *
 *   // reading them: f.values.role, or f.values (a plain object) straight into a query
 *
 * ── Two things that are deliberate ───────────────────────────────────────────
 *
 * ⚠️ **`empty` defaults to `"all"`, not `""`.** Phase 6.3 flagged this as the trap
 * that silently shows every filter as active: the `/sa/*` list pages carry an
 * explicit "All" row in each dropdown and their APIs read that sentinel. A page whose
 * empty value is the empty string must say `empty: ""` per key. Get it wrong and
 * nothing throws — you just get a chip that will not go away.
 *
 * The spec is read through a ref, so `options` may be async (an institute list that
 * arrives after first paint) without `reset` and `set` changing identity on every
 * render. That matters because they end up in `useEffect` dependency arrays.
 *
 * @param {Object} spec   { key: { label, options?, empty?, initial?, format? } }
 * @param {Function} [onChange]  called after any set/reset — usually `() => setPage(1)`
 */
const useListFilters = (spec, onChange) => {
  const specRef = useRef(spec);
  specRef.current = spec;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const emptyOf = (key) => {
    const e = specRef.current[key]?.empty;
    return e === undefined ? "all" : e;
  };

  /**
  * First render only. A key's `initial` wins over its `empty`, which is how a
  * DEEP-LINKED filter survives the migration: EnrollmentRequestsPage opens at
  * `?status=pending` and seeds its state from the query string. Without this the
  * hook would always start at `empty` and the link would silently stop working —
  * the page still renders, just unfiltered, which is the quietest possible
  * regression. `reset` deliberately goes to `empty`, NOT back to `initial`:
  * "Reset Filters" means clear them, not "return to how the URL found me".
  */
  const initial = useRef(null);
  if (initial.current === null) {
    initial.current = Object.fromEntries(
      Object.keys(spec).map((k) => {
        const conf = spec[k] || {};
        if (conf.initial !== undefined) return [k, conf.initial];
        return [k, conf.empty === undefined ? "all" : conf.empty];
      })
    );
  }

  const [values, setValues] = useState(initial.current);
  const [show, setShow] = useState(false);

  const set = useCallback((key, value) => {
    setValues((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    onChangeRef.current?.();
  }, []);

  const reset = useCallback(() => {
    setValues(
      Object.fromEntries(
        Object.keys(specRef.current).map((k) => {
          const e = specRef.current[k]?.empty;
          return [k, e === undefined ? "all" : e];
        })
      )
    );
    onChangeRef.current?.();
  }, []);

  const open = useCallback(() => setShow(true), []);
  const close = useCallback(() => setShow(false), []);

  /**
   * The single source the chip row, the trigger badge and the drawer's footer count
   * all read. A filter is active when its value differs from its own empty value.
   *
   * `format` exists for the case a raw value is not a label — a date range, or an id
   * whose display name lives elsewhere. Without it the label comes from `options`,
   * and failing that from the value itself, which is right for a free-text filter.
   */
  const activeFilters = useMemo(
    () =>
      Object.keys(spec)
        .filter((key) => values[key] !== emptyOf(key))
        .map((key) => {
          const conf = spec[key] || {};
          const value = values[key];
          const shown =
            conf.format?.(value) ??
            conf.options?.find((o) => o.value === value)?.label ??
            value;
          return {
            key,
            label: conf.label ? `${conf.label}: ${shown}` : String(shown),
            onRemove: () => set(key, emptyOf(key)),
          };
        }),
    // `spec` is recreated every render by most callers, so depending on it would
    // rebuild this every time and defeat the memo. `values` is what actually decides
    // the output; labels and options are read at build time from the current spec.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, set]
  );

  return {
    values,
    set,
    reset,
    show,
    setShow,
    open,
    close,
    activeFilters,
    count: activeFilters.length,

    /** Spread onto <FilterPanel>. */
    panelProps: {
      show,
      onHide: close,
      onReset: reset,
      appliedCount: activeFilters.length,
    },

    /** Spread onto <FilterSelectField />. */
    selectProps: (key) => ({
      label: spec[key]?.label,
      value: values[key],
      options: spec[key]?.options || [],
      onChange: (v) => set(key, v),
    }),
  };
};

export default useListFilters;
